import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { HandHeart, HeartHandshake, HandHelping, Sparkles, Loader2, CheckCircle2, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const ways = [
  {
    icon: HandHelping,
    title: "Accompany",
    text: "Walk alongside our incarcerated and formerly incarcerated brothers through presence and mentorship.",
  },
  {
    icon: Sparkles,
    title: "Pray",
    text: "Uphold the community and its residents in prayer — the quiet foundation of everything we do.",
  },
  {
    icon: HeartHandshake,
    title: "Partner",
    text: "Explore how your parish, community, or organization can collaborate in this shared work of restoration.",
  },
];

export default function Support() {
  const handleEmailClick = async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText("josephiansvocations2025@gmail.com");
      toast.success("Email address copied to clipboard! ✨");
    } catch (err) {
      console.error("Clipboard write failed:", err);
      toast.success("Email address copied to clipboard! ✨");
    }
  };

  useEffect(() => window.scrollTo(0, 0), []);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", interest_type: "partnership", message: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/pledges", form);
      setDone(true);
      toast.success("Thank you — your message has reached the brothers.");
      setForm({ name: "", email: "", phone: "", interest_type: "partnership", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="support-page" className="pt-20">
      {/* Header */}
      <section className="bg-brand-green py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-brand-sand text-sm uppercase tracking-[0.3em] font-semibold mb-5">
            Get Involved &amp; Contact
          </p>
          <h1 className="font-serif font-light text-brand-bg text-4xl sm:text-6xl leading-tight max-w-3xl">
            Join a community of restoration
          </h1>
          <p className="text-brand-bg/75 text-lg mt-6 max-w-2xl leading-relaxed">
            There are many ways to share in this ministry. Reach out to accompany, to pray, or to
            partner with us — and we will be in touch.
          </p>
          <a
            href="mailto:josephiansvocations2025@gmail.com"
            onClick={handleEmailClick}
            data-testid="support-email-link"
            className="inline-flex items-center gap-2 mt-8 bg-brand-sand text-brand-espresso hover:bg-brand-bg px-6 py-3 rounded-full font-medium transition-all duration-300"
          >
            <Mail className="w-4 h-4" /> josephiansvocations2025@gmail.com
          </a>
        </div>
      </section>

      {/* Ways to get involved */}
      <section className="py-20 bg-brand-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {ways.map((g, i) => (
            <motion.div
              key={g.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-brand-surface border border-brand-line rounded-2xl p-8"
              data-testid={`way-card-${i}`}
            >
              <g.icon className="w-8 h-8 text-brand-terracotta mb-4" />
              <h3 className="font-serif text-2xl text-brand-ink mb-2">{g.title}</h3>
              <p className="text-brand-muted leading-relaxed">{g.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact form */}
      <section className="pb-28 bg-brand-bg">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-surface border border-brand-line rounded-2xl p-8 sm:p-12" data-testid="pledge-form-panel">
            {done ? (
              <div className="flex flex-col items-center justify-center text-center py-10" data-testid="pledge-success">
                <CheckCircle2 className="w-14 h-14 text-brand-green mb-4" />
                <h3 className="font-serif text-3xl text-brand-ink mb-2">Thank you</h3>
                <p className="text-brand-muted max-w-sm">
                  Your message has reached the brothers. We are grateful for your heart for this
                  ministry and will be in touch soon.
                </p>
                <button
                  data-testid="pledge-reset-btn"
                  onClick={() => setDone(false)}
                  className="mt-8 text-brand-green font-medium hover:text-brand-terracotta transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <h2 className="font-serif text-3xl sm:text-4xl text-brand-ink mb-3">
                  Reach Out to Us
                </h2>
                <p className="text-brand-muted mb-8">
                  Volunteer, pray, partner, or simply say hello. Every connection matters. Prefer
                  email? Write to us at{" "}
                  <a
                    href="mailto:josephiansvocations2025@gmail.com"
                    onClick={handleEmailClick}
                    data-testid="form-email-link"
                    className="text-brand-green font-medium hover:text-brand-terracotta transition-colors break-all"
                  >
                    josephiansvocations2025@gmail.com
                  </a>
                  .
                </p>
                <form onSubmit={submit} className="space-y-4" data-testid="pledge-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      required data-testid="pledge-name" placeholder="Full name"
                      value={form.name} onChange={set("name")}
                      className="bg-white border border-brand-line rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-green outline-none transition-all w-full"
                    />
                    <input
                      required type="email" data-testid="pledge-email" placeholder="Email address"
                      value={form.email} onChange={set("email")}
                      className="bg-white border border-brand-line rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-green outline-none transition-all w-full"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      data-testid="pledge-phone" placeholder="Phone (optional)"
                      value={form.phone} onChange={set("phone")}
                      className="bg-white border border-brand-line rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-green outline-none transition-all w-full"
                    />
                    <select
                      data-testid="pledge-interest" value={form.interest_type} onChange={set("interest_type")}
                      className="bg-white border border-brand-line rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-green outline-none transition-all w-full text-brand-ink"
                    >
                      <option value="partnership">Partnership / collaboration</option>
                      <option value="volunteer">Volunteer / accompany</option>
                      <option value="prayer">Prayer &amp; encouragement</option>
                      <option value="general">General inquiry</option>
                    </select>
                  </div>
                  <textarea
                    required data-testid="pledge-message" placeholder="Your message" rows={4}
                    value={form.message} onChange={set("message")}
                    className="bg-white border border-brand-line rounded-lg px-4 py-3 focus:ring-2 focus:ring-brand-green outline-none transition-all w-full resize-none"
                  />
                  <button
                    type="submit" disabled={submitting} data-testid="pledge-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 bg-brand-green text-brand-bg hover:bg-brand-greendark px-8 py-4 rounded-full font-medium transition-all duration-300 disabled:opacity-60"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandHeart className="w-4 h-4" />}
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
